/**
 * Centralized API client with automatic token attachment and 401 refresh handling.
 *
 * Adapted from Trinity AI `apps/web/src/lib/api-client.ts` for Zuroq's BFF API.
 * Token helpers imported from `lib/auth-tokens` (shared single source of truth)
 * to avoid duplication and prevent future circular dependencies when auth.store
 * migrates from raw fetch to apiClient.
 */

import {
  getAccessToken,
  setAccessToken,
  getRefreshToken,
  setRefreshToken,
  clearAuthStorage,
} from './auth-tokens';

const API_BASE_URL = import.meta.env.VITE_BFF_URL || 'http://localhost:3010';

/**
 * Shape of error responses from the BFF/microservices.
 */
export interface ApiError {
  statusCode: number;
  message: string;
  error?: string;
}

/**
 * Typed error class for API client errors.
 * Preserves the HTTP status code and server-provided message.
 */
export class ApiClientError extends Error {
  statusCode: number;
  serverMessage: string;

  constructor(error: ApiError) {
    super(error.message);
    this.name = 'ApiClientError';
    this.statusCode = error.statusCode;
    this.serverMessage = error.message;
  }
}

/** Flag to prevent concurrent refresh attempts */
let isRefreshing = false;
/** Queue of requests waiting for token refresh to complete */
let refreshQueue: Array<{ resolve: (token: string) => void; reject: (err: Error) => void }> = [];

/**
 * Attempt to refresh the access token using the stored refresh token.
 * Returns the new access token on success, or null on failure.
 */
async function attemptTokenRefresh(): Promise<string | null> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return null;

  try {
    const response = await fetch(`${API_BASE_URL}/auth/v3/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });

    if (!response.ok) return null;

    const result = await response.json();
    const newAccessToken = result?.data?.token;
    const newRefreshToken = result?.data?.refreshToken;

    if (newAccessToken) {
      setAccessToken(newAccessToken);
      if (newRefreshToken) setRefreshToken(newRefreshToken);
      if (result?.data?.expires) {
        localStorage.setItem('tokenExpiry', result.data.expires);
      }
      return newAccessToken;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Handle 401 with token refresh. If a refresh is already in progress,
 * queues the request to retry after refresh completes.
 */
function handleTokenRefresh(): Promise<string> {
  if (isRefreshing) {
    return new Promise<string>((resolve, reject) => {
      refreshQueue.push({ resolve, reject });
    });
  }

  isRefreshing = true;
  return attemptTokenRefresh()
    .then((newToken) => {
      if (newToken) {
        refreshQueue.forEach(({ resolve }) => resolve(newToken));
        return newToken;
      }
      const err = new Error('Refresh failed');
      refreshQueue.forEach(({ reject }) => reject(err));
      throw err;
    })
    .finally(() => {
      isRefreshing = false;
      refreshQueue = [];
    });
}

/**
 * Core request function with automatic Bearer token attachment.
 * On 401 response, attempts token refresh using the refresh token.
 * If refresh fails, clears auth storage and redirects to /login.
 *
 * @param path - API path (e.g. '/auth/v3/login')
 * @param options - Standard RequestInit options
 * @param isRetry - Internal flag to prevent infinite retry loops
 * @returns Parsed JSON response body
 */
async function request<T>(path: string, options: RequestInit = {}, isRetry = false): Promise<T> {
  const url = `${API_BASE_URL}${path}`;
  const token = getAccessToken();

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  // Attach org context header when an active org is selected
  const activeOrgId = localStorage.getItem('activeOrgId');
  if (activeOrgId) {
    headers['X-Org-Id'] = activeOrgId;
  }

  const response = await fetch(url, { ...options, headers });

  // On 401, attempt refresh before giving up
  if (response.status === 401 && token && !isRetry) {
    try {
      const newToken = await handleTokenRefresh();
      if (newToken) {
        // Retry the original request with the new token
        return request<T>(path, options, true);
      }
    } catch {
      // Refresh failed — fall through to clear auth
    }

    clearAuthStorage();
    window.location.href = '/login';
    throw new ApiClientError({
      statusCode: 401,
      message: 'Session expired. Please log in again.',
    });
  }

  if (!response.ok) {
    let errorBody: ApiError;
    try {
      errorBody = await response.json();
    } catch {
      errorBody = {
        statusCode: response.status,
        message: response.statusText || 'Unknown error',
      };
    }
    throw new ApiClientError(errorBody);
  }

  // Handle 204 No Content
  if (response.status === 204) {
    return undefined as T;
  }

  return response.json();
}

/**
 * Centralized API client singleton with typed HTTP methods.
 *
 * Usage:
 *   const data = await apiClient.get<UserDTO>('/auth/v3/status');
 *   await apiClient.post('/documents/v1/upload', { file: ... });
 */
export const apiClient = {
  /** HTTP GET request */
  get: <T>(path: string) => request<T>(path, { method: 'GET' }),

  /** HTTP POST request */
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    }),

  /** HTTP PUT request */
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    }),

  /** HTTP PATCH request */
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: 'PATCH',
      body: body ? JSON.stringify(body) : undefined,
    }),

  /** HTTP DELETE request */
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
};
